import { CommonButton } from 'components/Buttons';
import React from 'react';
import utils from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { actionType, modalActionType } from 'utils/constants';
import selectors from 'state/selectors';
import services from 'services';
import useCheckBeforeAction from '../hooks';
import { lucyModuleKey } from 'utils/configs';
import { useField, useFormikContext } from 'formik';
import { FormValues, Q1Value } from 'models/documents';
import actions from 'state/actions';
import { useHistory } from 'react-router';

const GenerateDEM = () => {
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  const t = utils.intl.useTranslator();
  const saveDocument = utils.hooks.useSaveDocument();
  const { checkDocuments } = useCheckBeforeAction(actionType.SANITY_CHECK);
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const { setStatus } = useFormikContext<{ [key: string]: Q1Value | FormValues }>() || {};
  const programCode = useSelector(selectors.app.getProgramCode);
  const textTranslate = utils.app.textTranslated;
  const idProgram = useSelector(selectors.app.getProgramCode);
  const history = useHistory();

  const getProgramCode = (idProgram: number) => {
    return lucyModuleKey[idProgram as keyof typeof lucyModuleKey] || '';
  };
  const [fieldSubject] = useField<FormValues['subjectHeader']>('subjectHeader');
  const dispatch = useDispatch();
  const generateDem = async () => {
    const timestamp = utils.date.convertDate(new Date());

    try {
      const response = await services.generateDEM(records[0].protocol);

      utils.file.downloadFileFromBase64Source(
        `${fieldSubject.value?.name || ''}_DEM_${timestamp}`,
        response.data,
        'application/pdf',
        'pdf',
      );
      if (!protocol) return;
      const body = utils.documents.buildLeaseValidationRequest(protocol, idProgram, 'validate');
      const {
        data: {
          store: { RESPONSE },
        },
      } = await services.leaseValidate(body, 'module');
      utils.documents.handlePayloadUpdate(RESPONSE, protocol, dispatch);
      dispatch(actions.documents.setActiveDocumentId([]));
      utils.app.notify('success', t('dem-generated-successfully'), 10000);
      history.goBack();
    } catch (e) {
      console.error(e);
      utils.app.notify('fail', t('error-generating-dem'), 10000);
    }
  };

  const applyRules = async () => {
    try {
      if (protocol) {
        const lucyModule = getProgramCode(programCode);
        const { data } = await services.checkMandatoryFields({ protocol, rulesGroup: lucyModule }, 'module');
        const status = data.fields.map((el: any) => ({ idField: el.idField, status: el.fieldStatus }));
        setStatus(status);
        if (data.docStatus === 1) {
          utils.app.notify(
            'fail',
            textTranslate(data.docMessageStatus, t) || t('error-submitting-the-document'),
            10000,
          );
          data.docMessageStatusDetails?.length
            ? dispatch(
                actions.modal.openModal(modalActionType.documents.SHOW_RESPONSE, {
                  title: 'Status Details',
                  content: data.docMessageStatusDetails,
                }),
              )
            : dispatch(actions.modal.closeModal());

          return;
        } else if (data.docStatus === 2) {
          dispatch(
            actions.modal.openModal(modalActionType.documents.VALIDATION_CONFIRM_MODAL, {
              title: 'generate_dem_confirmation',
              subTitle: 'confirm_apply_rules_generate_dem',
              onConfirm: () => {
                generateDem();
                dispatch(actions.modal.closeModal());
              },
            }),
          );
          return;
        }
        generateDem();
      }
    } catch (e) {
      utils.app.notify('fail', textTranslate('error-submitting-the-document', t), 10000);
    }
  };

  const onClick = async () => {
    const docFree = await checkDocuments();
    if (!docFree) return;
    const val = await saveDocument();
    if (val === null || val === undefined) {
      return;
    }
    applyRules();
  };

  return (
    <CommonButton
      id="generate-dem-btn"
      action={onClick}
      scope="tertiary"
      value={t('generate-dem')}
      customIcon="file-pdf"
      disabled={!utils.user.isActionEnabled(records, actionType.GENERATE_DEM)}
    />
  );
};

export default GenerateDEM;
